<template>
    <div class="search-select">
        <div class="input-wrapper">
            <input
                type="text"
                ref="input"
                @focus="onInputFocus"
                @blur="onInputBlur"
                @keydown="onKeyDown"
                @keyup="onKeyUp"
                v-model="searchText">
            <div
                class="placeholder"
                v-if="placeholder"
                v-show="!searchText">
                {{ placeholder }}
            </div>
            <i class="fa fa-search"></i>
        </div>
        <div
            ref="dropdown"
            class="dropdown"
            v-show="dropdownOpen">
            <ul class="option-list">
                <li
                    :class="getOptionClasses(option)"
                    :key="option.value"
                    @mousedown.prevent=""
                    @mouseenter="highlightOption(option)"
                    @click="selectOption(option)"
                    v-for="option in filteredOptions">
                    {{ option.label }}
                </li>
            </ul>
            <div
                class="status-msg"
                v-show="optionsLoading">
                Loading...
            </div>
            <div
                class="status-msg"
                v-show="!optionsLoading && options.length === 0">
                No results
            </div>
        </div>
    </div>
</template>

<script>
    // TODO: we shouldn't accept an options array, we should use slots. The
    // current approach makes things hard to use scoped styles.

    function getTopPos(elem) {
        return elem.offsetTop;
    }

    function getScrollPos(elem) {
        return elem.scrollTop;
    }

    export default {
        props: {
            options: {
                /**
                 * Can either be a string array or an object array. If a string
                 * array, the strings will be used as both the and value and
                 * labels. If an object array, the `value` and `label` can be
                 * different.
                 */
                type: Array,
                required: true,
            },
            value: {
                /**
                 * If `multiple` is true, `value` should be an array. If false,
                 * `value` can be a string denoting the option `value`, or null
                 * if no option is selected.
                 */
                required: true,
                validator(value) {
                    return (Array.isArray(value) ||
                            typeof value === 'string' ||
                            value === null);
                },
            },
            multiple: {
                type: Boolean,
                default: false,
            },
            placeholder: {
                type: String,
                default: 'Please select',
            },
            blurOnSelect: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                dropdownOpen: false,
                searchText: '',
                highlightedIndex: 0,
                optionsLoading: false,
            };
        },

        methods: {
            onInputFocus() {
                this.openDropdown();
                this.resetHighlight();
            },

            onInputBlur() {
                this.closeDropdown();
            },

            onKeyDown(ev) {
                if (ev.key === 'ArrowUp') {
                    ev.preventDefault();
                    if (this.dropdownOpen) {
                        this.moveHighlightUp();
                    } else {
                        this.openDropdown();
                    }
                } else if (ev.key === 'ArrowDown') {
                    ev.preventDefault();
                    if (this.dropdownOpen) {
                        this.moveHighlightDown();
                    } else {
                        this.openDropdown();
                    }
                }
            },

            onKeyUp(ev) {
                if (ev.key === 'Escape') {
                    this.blurInput();
                } else if (ev.key === 'Enter') {
                    this.selectHighlightedOption();
                }
            },

            blurInput() {
                this.$refs.input.blur();
            },

            resetHighlight() {
                this.highlightedIndex = 0;
            },

            moveHighlightUp() {
                if (this.highlightedIndex <= 0) {
                    return;
                }
                this.highlightedIndex -= 1;
                // TODO: update scroll position
            },

            moveHighlightDown() {
                if (this.highlightedIndex >= this.filteredOptions.length - 1) {
                    return;
                }
                this.highlightedIndex += 1;
                // TODO: update scroll position
            },

            highlightOption(option) {
                const index = this.getFilteredOptionIndex(option);
                this.highlightedIndex = index;
            },

            selectOption(option) {
                const opVal = option.value;
                if (this.multiple) {
                    let newValue;
                    if (this.value.includes(opVal)) {
                        newValue = this.value.filter(v => v !== opVal);
                    } else {
                        newValue = this.value.concat(opVal);
                    }
                    this.$emit('input', newValue);
                } else {
                    this.$emit('input', option.value);
                }
                this.searchText = '';
                if (this.blurOnSelect) {
                    this.blurInput();
                }
            },

            selectHighlightedOption() {
                if (this.highlightedIndex < this.filteredOptions.length) {
                    const option = this.filteredOptions[this.highlightedIndex];
                    this.selectOption(option);
                }
            },

            toggleDropdown() {
                this.dropdownOpen = !this.dropdownOpen;
            },

            openDropdown() {
                this.dropdownOpen = true;
            },

            closeDropdown() {
                this.dropdownOpen = false;
            },

            setLoading(loading) {
                this.optionsLoading= loading;
            },

            getFilteredOptionIndex(option) {
                const opVal = option.value;
                return this.filteredOptions.findIndex(o => o.value === opVal);
            },

            getOptionClasses(option) {
                const index = this.getFilteredOptionIndex(option);
                return {
                    option: true,
                    highlighted: index === this.highlightedIndex,
                };
            },
        },

        computed: {
            normalizedOptions() {
                return this.options.map(option => {
                    if (typeof option === 'string') {
                        return {
                            value: option,
                            label: option,
                        };
                    }
                    return {
                        value: option.value,
                        label: option.label || option.value,
                    };
                });
            },

            filteredOptions() {
                function normalizeStr(str) {
                    return str.trim().toLowerCase();
                }
                const normSearchText = normalizeStr(this.searchText);
                return this.normalizedOptions.filter(option => {
                    const normOptionLabel = normalizeStr(option.label);
                    return normOptionLabel.includes(normSearchText);
                });
            },
        },

        watch: {
            searchText: function() {
                this.resetHighlight();
                if (!this.dropdownOpen && this.searchText.length > 0) {
                    this.openDropdown();
                }
            },
        },
    }
</script>

<style lang="scss" scoped>
    .search-select {
        height: 40px;
        line-height: 40px;
        overflow: visible;
        cursor: text;
        position: relative;
        background: #363636;
    }

    .input-wrapper {
        position: relative;
        height: 100%;

        input {
            background: none;
            border: none;
            color: #c0c0c0;
            display: block;
            width: 100%;
            height: 100%;
            padding: 0 35px 0 10px;

            &:focus {
                color: #f0f0f0;
            }
        }

        .placeholder {
            pointer-events: none;
            position: absolute;
            top: 0;
            height: 100%;
            padding: 0 10px;
            color: #888;
        }

        i {
            pointer-events: none;
            font-size: 16px;
            line-height: inherit;
            position: absolute;
            top: -1px; // search icon is one pixel too low
            right: 0;
            height: 100%;
            padding: 0 10px;
        }
    }

    .dropdown {
        position: absolute;
        top: 100%;
        background: #363636;
        border: 1px solid #666;
        width: 100%;
        max-height: 250px;
        overflow: auto;
    }

    .option-list {
        // Note: floated left to allow horizontal scrolling without jankiness.
        // To see what I mean, use a long option name that causes a horizontal
        // scrollbar to appear and remove this float; the child widths will be
        // restricted.
        float: left;
        list-style: none;
        padding: 0;
        margin: 0;
        min-width: 100%;
    }

    .option {
        white-space: nowrap;
        display: block;
        color: #ebebeb;
        cursor: pointer;
        height: 28px;
        line-height: 28px;
        padding: 0 10px;

        &.highlighted {
            background-color: #b0b0b0;
            color: #363636;
        }
    }

    .status-msg {
        padding: 0 10px;
    }
</style>



// WEBPACK FOOTER //
// index.vue?32be7e30